import * as React from 'react';
import {
  FormProvider,
  useForm,
  useFormContext,
  type DefaultValues,
  type FieldValues,
  type SubmitHandler,
  type UseFormReturn,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import { cn } from '@/lib/utils';

/**
 * Generic schema-driven form organism.
 *
 * Wraps react-hook-form's `useForm` + Zod resolver and exposes the form
 * context through both `FormProvider` (so the typed field helpers in
 * `./FormFields.tsx` can call `useFormContext()`) and the render-prop
 * `children` ({ form }) — for callers that want direct access without
 * reaching for the hook.
 *
 * Submission is wired through `handleSubmit`, so Enter inside an input
 * inside the form will submit. While the user-supplied `onSubmit` is
 * pending (awaited Promise) `formState.isSubmitting` flips to true,
 * which `useFormField()` exposes so `<button disabled>` can react.
 */
type FormProps<TSchema extends z.ZodTypeAny> = {
  schema: TSchema;
  defaultValues?: DefaultValues<z.infer<TSchema>>;
  onSubmit: (
    values: z.infer<TSchema>,
    form: UseFormReturn<z.infer<TSchema>>,
  ) => void | Promise<void>;
  children: (ctx: { form: UseFormReturn<z.infer<TSchema>> }) => React.ReactNode;
  className?: string;
  id?: string;
};

export function Form<TSchema extends z.ZodTypeAny>({
  schema,
  defaultValues,
  onSubmit,
  children,
  className,
  id,
}: FormProps<TSchema>): React.ReactElement {
  type Values = z.infer<TSchema>;

  const form = useForm<Values>({
    // zodResolver's generic plumbing fights inferred `Values` here; the
    // surface contract is correct — Values flows through `handleSubmit`,
    // `register`, and `formState` — so we accept the cast at this seam.
    resolver: zodResolver(schema as never) as never,
    defaultValues,
    mode: 'onTouched',
  });

  const handler: SubmitHandler<Values> = async (values) => {
    await onSubmit(values, form);
  };

  return (
    <FormProvider {...form}>
      <form
        id={id}
        noValidate
        onSubmit={(e) => void form.handleSubmit(handler)(e)}
        className={cn('flex flex-col gap-4', className)}
      >
        {children({ form })}
      </form>
    </FormProvider>
  );
}

/**
 * Field-level helper bundling react-hook-form's context with a stable
 * `id` for label/aria wiring. Field components (`TextField`,
 * `SelectField`, `SwitchField`) call this so the contract is uniform.
 */
export function useFormField<TValues extends FieldValues = FieldValues>(
  name: string,
): {
  form: UseFormReturn<TValues>;
  id: string;
  descriptionId: string;
  errorId: string;
  error: string | undefined;
  isSubmitting: boolean;
} {
  const form = useFormContext<TValues>();
  if (!form) {
    throw new Error(
      'Form fields must be rendered inside a <Form>. Wrap the field tree with the Form organism.',
    );
  }
  const reactId = React.useId();
  const slug = name.replace(/[^a-zA-Z0-9_-]/g, '-');
  const id = `${reactId}${slug}`;
  const fieldError = (form.formState.errors as Record<string, { message?: string } | undefined>)[
    name
  ];
  return {
    form,
    id,
    descriptionId: `${id}-desc`,
    errorId: `${id}-err`,
    error: fieldError?.message,
    isSubmitting: form.formState.isSubmitting,
  };
}
